import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import type { QueryKey } from '@tanstack/react-query';
import type { FileRequest, UturnFileDTO } from '@uturn/api/v1';
import { useDeleteDocumentById } from '@uturn/api/v2';
import { ShipmentDetailContext } from '@uturn/portal/modules/shipments/providers/shipment-detail';
import type { FormValues } from '@uturn/portal/modules/shipments/schema';
import { isFieldReadOnly } from '@uturn/portal/modules/shipments/utils';
import { queryClient } from '@uturn/portal/query';
import type { Document } from '@uturn/portal/types/shipment';
import { normalizeString } from '@uturn/portal/utils/normalize-string';
import {
	Button,
	FormControl,
	FormField,
	FormFieldReadOnly,
	FormItem,
	FormMessage,
	Icon,
	Label,
	Sheet,
	SheetContent,
	SheetTrigger,
	sonner,
	Textarea,
} from '@uturn/ui-kit';
import { useContext, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AddDocument } from './add-document';
import { ConfirmDeleteDocumentModal } from './confirm-delete-document-modal';
import { ShipmentDocumentTile } from './shipment-document-tile';

export type AdditionalInformationProps = {
	initDocuments: UturnFileDTO[];
	setDocuments: (documents: Document[]) => void;
	documentQueryKey?: QueryKey;
	/**
	 * When provided (update flow), the document is uploaded immediately and the
	 * optimistic preview tile is rolled back if the upload fails. When omitted
	 * (create flow), the document stays pending and is bundled into the create
	 * payload on submit.
	 */
	uploadDocument?: (
		file: File,
		fileRequest: Partial<FileRequest>,
	) => Promise<void>;
};

const getFormatedDocumentKey = (fileType: string, description: string) =>
	`${normalizeString(fileType)}|${normalizeString(description)}`;

export function AdditionalInformation({
	initDocuments = [],
	setDocuments,
	documentQueryKey,
	uploadDocument,
}: AdditionalInformationProps) {
	const { t } = useTranslation();
	const { control, getValues } = useFormContext<FormValues>();

	const { readOnlyFields } = useContext(ShipmentDetailContext);

	const [localDocuments, setLocalDocuments] = useState<Document[]>([]);

	const [openDeleteFileModal, setOpenDeleteFileModal] = useState(false);
	const [toDeleteDocument, setToDeleteDocument] = useState<
		UturnFileDTO | undefined
	>(undefined);

	// Track UUIDs being deleted to hide them immediately
	const [deletingUuids, setDeletingUuids] = useState<Set<string>>(new Set());
	const { mutate: deleteDocument } = useDeleteDocumentById();

	const documentsLocalKeys = useMemo(
		() =>
			new Set(
				localDocuments.map((doc) =>
					getFormatedDocumentKey(
						doc.fileRequest?.fileType ?? '',
						doc.fileRequest?.description ?? '',
					),
				),
			),
		[localDocuments],
	);

	const pendingDocuments = localDocuments;

	// Filter out BE docs that match local docs OR are being deleted
	const attachedDocuments = useMemo(() => {
		return initDocuments.filter((doc) => {
			if (doc.uuid && deletingUuids.has(doc.uuid)) {
				return false;
			}
			const key = getFormatedDocumentKey(
				doc.fileTypeCode ?? '',
				doc.description ?? '',
			);
			return !documentsLocalKeys.has(key);
		});
	}, [initDocuments, documentsLocalKeys, deletingUuids]);

	const hasDocuments = attachedDocuments.length + pendingDocuments.length > 0;

	const saveDocument = async (
		file: File,
		fileRequest: Partial<FileRequest>,
	) => {
		const newDoc = { file, fileRequest };

		if (uploadDocument) {
			// Update flow: upload immediately without an optimistic tile. On success
			// the doc shows up as an "Attached" tile via the refetch; on failure only
			// the error toast is shown (from the upload handler) — so we never flash a
			// preview that then disappears.
			try {
				await uploadDocument(file, fileRequest);
			} catch {
				// Error toast is surfaced by the upload handler.
			}
			return;
		}

		// Create flow: keep the doc pending; it's bundled into the create payload.
		const updatedDocs = [...localDocuments, newDoc];
		setLocalDocuments(updatedDocs);
		setDocuments(updatedDocs);
	};

	const removeLocalFile = async (index: number) => {
		const file = localDocuments[index];
		const updatedDocs = localDocuments.filter((_, i) => i !== index);
		setLocalDocuments(updatedDocs);
		setDocuments(updatedDocs);

		// find corresponding document by type/description in BE data
		const matched = initDocuments.find(
			(d) =>
				normalizeString(d.fileTypeCode ?? '') ===
					normalizeString(file.fileRequest?.fileType ?? '') &&
				normalizeString(d.description ?? '') ===
					normalizeString(file.fileRequest?.description ?? ''),
		);

		const uuid = matched?.uuid;
		if (uuid) {
			setDeletingUuids((prev) => new Set(prev).add(uuid));

			deleteDocument(
				{ documentId: uuid },
				{
					onSuccess: () => {
						queryClient.invalidateQueries({ queryKey: documentQueryKey });
						sonner.success(
							t(
								'pages.shipment-detail.modal.delete-document.submit.success.description',
								'Document deleted successfully.',
							),
						);
					},
					onError: () => {
						// Restore visibility on error
						setDeletingUuids((prev) => {
							const next = new Set(prev);
							next.delete(uuid);
							return next;
						});
						sonner.error(
							t(
								'pages.shipment-detail.modal.delete-document.submit.error.description',
								'Error deleting document',
							),
						);
					},
				},
			);
		}
	};

	return (
		<>
			<div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-6">
				<div className="sm:col-span-full">
					<h3 className="font-heading text-lg font-medium tracking-tight">
						{t(
							'pages.create_shipment.shipment-details.additional_info.heading',
							'Additional information',
						)}
					</h3>
				</div>
				<div className="sm:col-span-full">
					<FormField
						control={control}
						name="details"
						render={({ field }) => (
							<FormItem>
								{isFieldReadOnly(readOnlyFields, 'details') ? (
									<FormFieldReadOnly value={getValues('details')} />
								) : (
									<FormControl>
										<Textarea
											{...field}
											placeholder={t(
												'components.shipment.public_field.placeholder',
												'This information can be seen by all carriers on the UTURN platform.',
											).toString()}
										/>
									</FormControl>
								)}
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				{hasDocuments && (
					<div className="flex flex-col gap-2 sm:col-span-4 md:col-span-3">
						<Label>
							{t(
								'pages.create_shipment.shipment-details.additional_info.documents',
								'Documents',
							)}
						</Label>
						{attachedDocuments
							.filter((d) => !!d.uuid)
							.map((d) => (
								<ShipmentDocumentTile
									key={d.uuid}
									uuid={d.uuid}
									title={d.fileTypeDescription ?? 'Document'}
									subtitle={d.description ?? ''}
									badge={{ label: 'Attached', variant: 'secondary' }}
									canDownload
									onDelete={() => {
										setToDeleteDocument(d);
										setOpenDeleteFileModal(true);
									}}
								/>
							))}
						{pendingDocuments.map((doc, i) => (
							<ShipmentDocumentTile
								key={`${doc.fileRequest?.fileName ?? 'file'}-${i}`}
								title={doc.fileRequest?.fileType ?? 'Document'}
								subtitle={doc.fileRequest?.description ?? ''}
								filename={doc.fileRequest?.fileName ?? doc.file?.name}
								badge={{ label: 'New', variant: 'default' }}
								onDelete={() => removeLocalFile(i)}
							/>
						))}
					</div>
				)}
				{isFieldReadOnly(readOnlyFields, 'details') ? null : (
					<div className="sm:col-span-full">
						<Sheet>
							<SheetTrigger asChild>
								<Button variant="secondary" size="sm" formNoValidate>
									<Icon icon={faPlus} className="mr-1.5" />
									{t(
										'pages.create_shipment.shipment-details.additional_info.add_doc',
										'Add document',
									)}
								</Button>
							</SheetTrigger>
							<SheetContent>
								<AddDocument saveDocument={saveDocument} />
							</SheetContent>
						</Sheet>
					</div>
				)}
			</div>
			<ConfirmDeleteDocumentModal
				openModal={openDeleteFileModal}
				setOpenModal={setOpenDeleteFileModal}
				documentQueryKey={documentQueryKey}
				toDeleteDocument={toDeleteDocument}
			/>
		</>
	);
}
